import { observer } from 'mobx-react';
import React from 'react';
import { Alert, Form, FormGroup, Input, InputGroup } from 'reactstrap';
import BusyButton from '~/components/BusyButton';
import ErrorList from '~/components/ErrorList';
import { LoginStore } from './LoginStore';
import { AsyncButton } from '~/components/AsyncButton';

interface VerificationFormProps {
    store: LoginStore;
}

@observer
export class VerificationForm extends React.Component<VerificationFormProps> {
    private codeInputRef = React.createRef<HTMLInputElement>();

    focusCodeInput() {
        if (this.codeInputRef.current) {
            this.codeInputRef.current.focus();
        }
    }

    async sendCodeViaSMS() {
        this.props.store.loginForm.clearErrors();
        try {
            await this.props.store.sendCodeViaSMS();
        } catch (error) {
            this.props.store.loginForm.setLastError(error);
        }
    }

    async sendCodeViaEmail() {
        this.props.store.loginForm.clearErrors();
        try {
            await this.props.store.resendEmail();
        } catch (error) {
            this.props.store.loginForm.setLastError(error);
        }
    }

    render() {
        const { store } = this.props;
        const { loginForm } = store;

        return (
            <>
                <div className="preLoginTitle">
                    <h2>Account Verification</h2>
                </div>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void loginForm.doSubmit(() => store.doLogin(), { errorSummary: 'Unable to log in' });
                    }}
                >
                    <FormGroup className="mb-3">
                        <label className="form-label" htmlFor={loginForm.idFor('email')}>
                            Email address
                        </label>
                        <Input
                            id={loginForm.idFor('email')}
                            readOnly={true}
                            name="email"
                            autoComplete="username"
                            value={loginForm.values.email}
                        />
                        <ErrorList errors={loginForm.errorsFor('email')} />
                    </FormGroup>

                    {store.verificationAlert && (
                        <Alert color={store.verificationAlert.type} role="alert">
                            {store.verificationAlert.message}
                        </Alert>
                    )}

                    <FormGroup className="mb-3">
                        <label className="form-label" htmlFor={loginForm.idFor('code')}>
                            Verification Code
                        </label>
                        <InputGroup>
                            <Input
                                id={loginForm.idFor('code')}
                                innerRef={this.codeInputRef}
                                type="text"
                                placeholder="012345"
                                maxLength={6}
                                minLength={6}
                                className="monospace"
                                autoFocus
                                autoComplete="one-time-code"
                                {...loginForm.inputProps('code')}
                            />
                            {store.codeInput === 'email' && (
                                <AsyncButton
                                    disabled={loginForm.isSubmitting}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        return this.sendCodeViaEmail().then(() => this.focusCodeInput());
                                    }}
                                >
                                    Resend Email
                                </AsyncButton>
                            )}
                        </InputGroup>
                        <ErrorList errors={loginForm.errorsFor('code')} />
                    </FormGroup>
                    {store.showSendSMS ? (
                        <AsyncButton
                            autoFocus
                            disabled={loginForm.isSubmitting}
                            className="w-100 mb-3"
                            onClick={(e) => {
                                e.preventDefault();
                                return this.sendCodeViaSMS().then(() => this.focusCodeInput());
                            }}
                        >
                            Send via SMS
                        </AsyncButton>
                    ) : (
                        <div className="forgotPassword text-muted mb-3">
                            <button
                                type="button"
                                className="linkButton text-muted"
                                onClick={() => store.handleShowSendSMS()}
                            >
                                Unable to find your code?
                            </button>
                        </div>
                    )}

                    <div className="loginActions">
                        <BusyButton
                            type="submit"
                            busy={loginForm.isSubmitting}
                            color="primary"
                            size="lg"
                            className="w-100"
                        >
                            <i className="icon ion-md-lock me-2" />
                            Log in
                        </BusyButton>
                    </div>

                    <div className="text-center mt-2">
                        <button
                            type="button"
                            className="linkButton text-muted"
                            onClick={() => store.handleBackToLogin()}
                        >
                            <i className="icon ion-md-arrow-back me-1" />
                            Login with different account
                        </button>
                    </div>
                </Form>
            </>
        );
    }
}
